import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "./FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { SingleAddressForm } from "./SingleAddressForm";
import { AtticusMySQL } from "../../../apis/AtticusMySQL";
import { Place } from "../../../models/Place";
import { estateApi } from "../../../apis";
import { State } from "../../../models/Geography";
import { ChangeCountryDialog } from "./ChangeCountryDialog";
import { UserProfile, UserProfileJSON } from "models/UserProfile";
import { Languages } from "models/User";
import { AssetRealEstate } from "../../../models/AssetDetail";
import { AssetContext } from "../../../AssetContextWrapper";
import { PlaceAPI } from "../../../apis/PlaceAPI";
import { isLocal } from "../../../constants";
import { UserAPI } from "apis/UserAPI";
import { UserProfileAPI } from "apis/UserProfileAPI";

export enum Outcomes {
  address = "Address",
  noAddress = "noAddress",
  skipToEnd = "skipToEnd",
}

interface Props {
  title?: string;
  subTitle?: string;
  initWithAddressInputOnly?: boolean;// NOTE: SingleAddressForm has two modes: address & country/state
}

export const PrimaryHomeAssetScreen = ({
  title,
  subTitle,
  initWithAddressInputOnly = false,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const { screenName } = useParams();
  const {
    flow,
    activeEstate,
    setActiveEstate,
    states,
    counties,
    countyTypes,
    countries,
    user,
    setUser,
  } = useAuthContext()
  const { categories, assetsLoaded, refreshAssets } = useContext(AssetContext);
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const [initPlace, setInitPlace] = useState<Place>();
  const [initFromPlaceDbTable, setInitFromPlaceDbTable] = useState(false);
  const [placeCheckComplete, setPlaceCheckComplete] = useState(false);
  const [initialValuesInitialized, setInitialValuesInitialized] = useState(false);
  const [countryChangeDialogVisible, setCountryChangeDialogVisible] = useState(false);
  const [locationCountryId, setLocationCountryId] = useState<number>();
  const [userCountryId, setUserCountryId] = useState<number>();
  const [initCountryId, setInitCountryId] = useState<number>();
  const [initStateId, setInitStateId] = useState<number>();

  useEffect(() => {
    (async () => {
      if (!assetsLoaded) {
        return
      }

      let place = undefined;
      
      const myUserCountryId = user?.profile?.countryId ? parseInt(user?.profile?.countryId, 10) : undefined
      if (myUserCountryId) {
        setUserCountryId(myUserCountryId);
      }

      // check if this estate as a primary home real estate asset set
      if (activeEstate && activeEstate?.primaryRealEstate) {
        place = new Place(
          activeEstate?.primaryRealEstate?.id,
          undefined,
          (activeEstate?.primaryRealEstate?.detail as AssetRealEstate)?.getFullAddress(
            states,
            countries
          ),
          (activeEstate?.primaryRealEstate?.detail as AssetRealEstate)?.google_places_id
        );
        setInitPlace(place);
        setLocationCountryId(
          (activeEstate?.primaryRealEstate?.detail as AssetRealEstate)?.countries_id
        );
      }
      // if customer doesn't have a assets_real_estate record check places table
      if (!place && activeEstate?.id) {
        // check if this estate has a primary place set
        const primaryPlace = PlaceAPI.singlePrimaryPlace(
          await PlaceAPI.getPlaces(activeEstate?.id)
        );
        if (primaryPlace) {
          setInitPlace(primaryPlace);
          setInitFromPlaceDbTable(true);
        }
      }
      setPlaceCheckComplete(true);
    })();
  }, [assetsLoaded, activeEstate?.primaryRealEstate]);

  const handleDialogComplete = async (
    countryId?: number,
    languageCode?: string
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");
    if (!user?.id) throw Error("couldn't determine user id");
    let outcome = Outcomes.noAddress;

    setCountryChangeDialogVisible(false);

    if (countryId != userCountryId) {
      // USER WANTS TO CHANGE THE COUNTRY TO MATCH THE PLACE.
      const editedProfileJson: UserProfileJSON = {};

      // Set New Country ID
      editedProfileJson.countries_id = countryId?.toString();

      // Set New Language Code
      if (languageCode) {
        try {
          const langId = languageCode == "en" ? Languages.en : Languages.fr;

          editedProfileJson.languages_id = langId.toString();
        } catch {
          throw "Invalid language code";
        }
      }

      // Perform API Call to Edit User Profile with new Country and Language
      await AtticusMySQL.updateUserProfile(user.id, editedProfileJson);

      // Now that the user's changed, let's get the latest version of the user into context.
      user.profile = UserProfile.fromJSON(editedProfileJson) || new UserProfile()
      setUser(user)

      outcome = Outcomes.address;
      flow?.navigateToNext(currentScreen, outcome, navigate);
    }
    return true;
  };

  const handleComplete = async (
    place?: Place,
    countryId?: number,
    stateId?: number
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");
    if(!activeEstate?.id || !user?.id) {
      return false
    }
    let outcome = Outcomes.noAddress;

    if (place) {
      // User is providing a Placse using Google Place API
      try {
        if (place?.placeId !== initPlace?.placeId || initFromPlaceDbTable) {
          const asset = place.toRealEstateAsset(
            activeEstate?.id || undefined,
            states,
            counties,
            countyTypes,
            countries,
            categories
          );
          if (initFromPlaceDbTable) {
            // if transitioning from places table to asset table delete the previous Place record
            if (initPlace?.id && activeEstate?.id) {
              await PlaceAPI.deletePlace(activeEstate?.id, initPlace.id);
            }
            // clear the id because we want to create a new asset record
            asset.id = undefined;
          }
          // save to db
          await estateApi.saveAsset(asset,activeEstate.id, user.id);

          // update the client data store
          await refreshAssets?.(true); // exclude fi

          // set the countryId and stateId on the client-side estate "database"
          if (place.state && place.country) {
            let theState = states?.find(
              (state: State) => state.code === place.state
            );
            if(isLocal && !theState && states) {
              theState = states[0]
            }

            // Get the countryID from the Place
            // Have to do this to immediately access value before re-rendering
            const tempLocationCountryId = place.country === "US" ? 1 : 2;
            setLocationCountryId(tempLocationCountryId);

            if (tempLocationCountryId && userCountryId && tempLocationCountryId != userCountryId) {
              // If the user's country is not the same as the Place's country,
              // then we need to update the user's country
              setCountryChangeDialogVisible(true);
              throw Error("Country mismatch");
            } else {
              if (theState) {
                // set the state and country on estate record
                const estate = activeEstate
                estate.stateId = theState?.id;
                estate.countryId = place.country === "US" ? 1 : 2;

                try {
                  await estate.saveEstate();
                  setActiveEstate(estate);
                } catch (error) {
                  throw new Error("could not save data");
                }
              } else {
                throw Error("could not determine state id from place");
              }
            }
          } else {
            throw Error("did not receive state and/or country place");
          }
        } else {
          // don't need to update the database record
        }
        outcome = Outcomes.address;
      } catch (error) {
        console.error(error as Error);
        throw Error(`Saving address failed. ${(error as any).message}`);
      }
    } else {
      // User is inputting the country and state without an address

      // Check to see if selected country matches user's country.
      if (countryId && userCountryId && countryId != userCountryId) {
        setLocationCountryId(countryId);
        setCountryChangeDialogVisible(true);

        throw Error("Country mismatch"); // where is this caught?
      } else if(activeEstate?.id) {
        const estate = activeEstate

        // Update the user's profile to set state/country
        const myUser = user 
        if(!myUser.profile){
          myUser.profile = new UserProfile()
          myUser.profile.userId = myUser.id
        }
        myUser.profile.countryId = (countryId || estate.countryId)?.toString()
        myUser.profile.stateId = (stateId || estate.stateId)?.toString()
        if(myUser.profile.stateId && !myUser.profile.countryId) {
          myUser.profile.stateId = undefined
        }
        setUser(myUser)
        await UserProfileAPI.update(myUser.profile.toJSON())


        // Update the estate
        estate.countryId = countryId || estate.countryId || undefined
        estate.stateId = stateId || estate.stateId || undefined
        if(estate.stateId && !estate.countryId) {
          estate.stateId = undefined
        }

        if (initPlace?.id && activeEstate?.id) {
          // previously had place set, delete it
          await estateApi.deleteAsset(initPlace.id, activeEstate.id);
          await refreshAssets?.(true); // exclude fi);
        }
        try {
          await estate.saveEstate();
          setActiveEstate(estate);
        } catch (error) {
          throw new Error("could not save data");
        }
      }
    }

    // TODO: confirm we want to shorten on-boarding for forms app customers
    // if (user?.origin === CustomerOrigin.FormsApp) {
    //   outcome = Outcomes.skipToEnd;
    // }
    flow?.navigateToNext(currentScreen, outcome, navigate);
    return true;
  };

  const handleClear = async (): Promise<void> => {
    console.log("clear address");
  };

  useEffect(() => {
    if(!user?.id) {
      return
    }
    if(!activeEstate?.id) {
      return
    }
    setInitialValuesInitialized(true)
    
    if (!initWithAddressInputOnly) {
      const myInitCountryId =
        activeEstate?.countryId && activeEstate.countryId == userCountryId
          ? activeEstate.countryId
          : !isNaN(parseInt(user?.profile?.countryId || "")) 
          ?  parseInt(user?.profile?.countryId || "") : userCountryId ||undefined;
      const myInitStateId = activeEstate?.stateId  
        ? activeEstate.stateId 
        : !isNaN(parseInt(user?.profile?.stateId || "")) 
        ? parseInt(user?.profile?.stateId || "") : undefined;
      setInitCountryId(myInitCountryId)
      setInitStateId(myInitStateId)
    }
  }, [user?.profile, activeEstate?.countryId, activeEstate])

 
  let personalizedTitle =
    title?.replace(
      "#deceasedFirstName#",
      activeEstate?.deceasedFirstName || ""
    ) || "Where did the deceased live (for tax and legal purposes)?";

  if (initPlace) {
    personalizedTitle = "Just making sure... " + personalizedTitle;
  }

  return currentScreen ? (
    <FlowScreenWrapper title={personalizedTitle} subTitle={subTitle}>
      {initialValuesInitialized && placeCheckComplete ? (
        <>
         <SingleAddressForm
            initPlace={initPlace}
            initCountryId={initCountryId} // does not work
            initStateId={initStateId}  // does not work
            onComplete={handleComplete}
            onClear={handleClear}
            onBack={() => navigate(-1)}
          />

          <ChangeCountryDialog
            open={countryChangeDialogVisible}
            handleClose={() => {
              setCountryChangeDialogVisible(false);
            }}
            onComplete={handleDialogComplete}
            newCountryId={locationCountryId}
            userCountryId={userCountryId}
          />
        </>
      ) : <></>}

    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
